import { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import type { RootState } from 'app/store';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import {
  flattenErrors,
  setResolveOpen,
  setResolutionInProgress,
  showResolveSummary,
  clearResolveSummary,
  type FlatTunnelErrors,
} from 'app/slices/ahuValidationSlice';
import {
  selectSection,
  setCurrentTab,
  setExpandedAccessory,
  setAccessoryOpenSubtab,
  openRatingRequest,
} from 'app/slices/ahuBoardSlice';
import type { IAhuBoardSection, IAhuBoardTunnel } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import {
  openAccessorySelectionDialog,
  SELECTABLE_ACCESSORY_TYPES,
} from '../../forms/accessory/openAccessorySelectionDialog';
import {
  transformErrors,
  type ResolveListItem,
  type UpsSectionContext,
} from './resolveCatalog';
import { applyResolution } from './applyResolution';

/**
 * Phase 6e1 — Error Manager window contents (§8.1/§8.1.1, error.log.js +
 * error.log.tmpl + error.log.resolution.* — markup faithful; the debug
 * `data-id="kkkkk"` dropped per [NEG]#21; `error_log_ctrl` template flags
 * always-falsy per [NEG]#43 → not rendered).
 *
 * Rows render grouped AHU-global → per-section accessories → components →
 * dmounts with `li#massage_error-item` (id reused across rows — selector
 * contract, kept) + the §8.1 data attributes. Row click/hover drive the §13
 * store-driven receivers + canvas events; the smart-resolve panel mirrors the
 * list 1:1 and APPLY runs the §8.1.1 pipeline.
 */

interface ErrorManagerProps {
  tunnels: IAhuBoardTunnel[];
  sections: IAhuBoardSection[];
}

const ErrorManager = ({ tunnels, sections }: ErrorManagerProps) => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const errors = useAppSelector((s) => s.ahuValidation.errors);
  const count = useAppSelector((s) => s.ahuValidation.count);
  const resolveOpen = useAppSelector((s) => s.ahuValidation.resolveOpen);
  const resolutionInProgress = useAppSelector((s) => s.ahuValidation.resolutionInProgress);
  const resolveSummary = useAppSelector((s) => s.ahuValidation.resolveSummary);
  const countsByType = useAppSelector((s) => s.ahuValidation.countsByType);

  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const [selections, setSelections] = useState<Record<number, string>>({});
  const [percent, setPercent] = useState(0);
  const [showError, setShowError] = useState(false);
  const [forceHoverIndex, setForceHoverIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const resolveListRef = useRef<HTMLUListElement | null>(null);
  const clickTimer = useRef<number | null>(null);

  // fan type-3 / coil duty maps for checkUpsSectionType (legacy reads the
  // stateStorage component models; here the bootstrap-cached details).
  const [fanType3BySection, setFanType3BySection] = useState<Record<number, boolean>>({});
  const [coilDutyOkBySection, setCoilDutyOkBySection] = useState<Record<number, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    const subs: Array<{ unsubscribe(): void }> = [];
    const fanMap: Record<number, boolean> = {};
    const coilMap: Record<number, boolean> = {};
    const promises = sections.map((section) => {
      const sub = dispatch(ahuBoardApi.endpoints.getSectionComponents.initiate(section.id));
      subs.push(sub);
      return sub
        .unwrap()
        .then((rows) => {
          const fan = rows.find((r) => r.type === 'fan');
          const coil = rows.find((r) => r.type === 'coil');
          const inner: Array<Promise<void>> = [];
          if (fan) {
            const fanSub = dispatch(ahuBoardApi.endpoints.getAhuFanDetail.initiate(fan.id));
            subs.push(fanSub);
            inner.push(
              fanSub
                .unwrap()
                .then((detail) => {
                  try {
                    const params = JSON.parse(
                      String((detail as Record<string, unknown>).fan_params ?? '{}')
                    ) as { type_id?: number };
                    fanMap[section.id] = params?.type_id === 3;
                  } catch {
                    fanMap[section.id] = false;
                  }
                })
                .catch(() => undefined)
            );
          }
          if (coil) {
            const coilSub = dispatch(ahuBoardApi.endpoints.getAhuCoilDetail.initiate(coil.id));
            subs.push(coilSub);
            inner.push(
              coilSub
                .unwrap()
                .then((detail) => {
                  coilMap[section.id] = (detail.duty_id ?? null) !== 2;
                })
                .catch(() => undefined)
            );
          }
          return Promise.all(inner).then(() => undefined);
        })
        .catch(() => undefined);
    });
    Promise.all(promises).then(() => {
      if (!cancelled) {
        setFanType3BySection(fanMap);
        setCoilDutyOkBySection(coilMap);
      }
    });
    return () => {
      cancelled = true;
      subs.forEach((s) => s.unsubscribe());
    };
  }, [sections, dispatch]);

  const flat = useMemo(() => flattenErrors(errors), [errors]);

  // checkUpsSectionType (§8.1.1 — fan/plenum/coil upstream analysis).
  const ctx: UpsSectionContext = useMemo(
    () => ({
      checkUpsSectionType: (item, sectionId, tunnelId) => {
        const error = String(item.error ?? '');
        if (sectionId == null || tunnelId == null) return error;
        const tunnel = tunnels.find((t) => t.id === tunnelId);
        const list = sections
          .filter((s) => s.tunnel_id === tunnelId)
          .sort((a, b) => (a.order_rate ?? 0) - (b.order_rate ?? 0));
        const idx = list.findIndex((s) => s.id === sectionId);
        if (idx === -1) return error;
        const upsIdx = idx + (tunnel?.airflow_direction_id === 1 ? -1 : 1);
        const upsSection = list[upsIdx];
        const current = list[idx];
        const currentType = String(current?.type ?? '');
        const upsType = String(upsSection?.type ?? '');
        const isFan = currentType === 'fan';
        const isUpsFanInvalid =
          upsType === 'fan' && !(upsSection && fanType3BySection[upsSection.id]);
        const isUpsInvalid = upsType === 'plenum' || upsType === 'coil' || isUpsFanInvalid;
        void coilDutyOkBySection;
        if (isFan) {
          if (fanType3BySection[sectionId]) return `${error}_inc`;
          if (isUpsInvalid) return `${error}_inc_ups`;
          return error;
        }
        if (isUpsInvalid) return `${error}_inc_ups`;
        return error;
      },
    }),
    [tunnels, sections, fanType3BySection, coilDutyOkBySection]
  );

  const { list: resolveList } = useMemo(
    () => transformErrors(flat, ctx),
    [flat, ctx]
  );

  const showResolution = resolveList.some((e) => e.resovable);
  const isChecked = (idx: number) => checked[idx] ?? true;
  const checkedCount = resolveList.filter((e, i) => e.resovable && isChecked(i)).length;

  // Resolve-summary 5 s auto-clear (`#resolved-errors-information`, parity 153).
  useEffect(() => {
    if (!resolveSummary) return undefined;
    const node = document.getElementById('resolved-errors-information');
    if (node) {
      const counts = [
        resolveSummary.prev.do_not_generate - resolveSummary.current.do_not_generate,
        resolveSummary.current.do_not_generate,
        resolveSummary.prev.generate - resolveSummary.current.generate,
        resolveSummary.current.generate,
      ];
      ['errors_resolved', 'errors_unresolved', 'warnings_resolved', 'warnings_unresolved'].forEach(
        (cls, idx) => {
          const el = node.querySelector(`.${cls}`);
          if (el) el.textContent = String(counts[idx] || 0);
        }
      );
      node.querySelector('.errors-row')?.classList.toggle('hide', counts[0] + counts[1] < 1);
      node
        .querySelector('.warnings-row')
        ?.classList.toggle('hide', counts[2] + counts[3] < 1);
      node.classList.add('active');
    }
    const timer = window.setTimeout(() => {
      node?.classList.remove('active');
      dispatch(clearResolveSummary());
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [resolveSummary, dispatch]);

  // ---- row click (§8.1 errorRowClick — §13 receivers store-driven) ----
  const sectionTintType = useMemo(() => {
    const map: Record<number, string> = {};
    flat.forEach((tunnelErr) => {
      tunnelErr.sections.forEach((section) => {
        [...section.components, ...section.accessories].forEach((item) => {
          if (item.message && map[section.id] !== 'do_not_generate') {
            map[section.id] = item.type ?? '';
          }
        });
      });
    });
    return map;
  }, [flat]);

  const rowClick = (args: {
    sectionId: number | null;
    tab: string;
    accessoryId: number | null;
    accessoryType?: string;
    error?: string;
    message?: string;
  }) => {
    document.getElementById('error_log')?.classList.add('clicked');
    window.setTimeout(() => {
      document.getElementById('error_log')?.classList.remove('clicked');
    }, 550);
    dispatch(setResolveOpen(false));
    const isForTunnel =
      args.error === 'does_not_fit_in_height' || args.error === 'does_not_fit_in_width';
    const tab = isForTunnel ? 'ahu' : args.accessoryId ? 'accessories' : 'components';
    // data-tab navy click → store tab switch (§13 rows 4/5/18).
    if (args.tab === '#show_sections' && !isForTunnel) dispatch(setCurrentTab('sections'));
    if (args.accessoryId != null) {
      const accessoryId = args.accessoryId;
      if (clickTimer.current) window.clearTimeout(clickTimer.current);
      clickTimer.current = window.setTimeout(() => {
        dispatch(setExpandedAccessory(accessoryId));
        dispatch(setAccessoryOpenSubtab({ accessoryId, subtab: 'mounting' }));
        // error.log.js:238 — an `isSelectable` row clicks the accessory form's
        // `label.active-mode`, i.e. its "Select" button, so an error row for a
        // damper/louver/hood/opening lands in the SELECTION dialog. 6e read
        // that label as the mounting popup (the same misreading as the canvas
        // dblclick, canvas.view3d.js:1023).
        if (args.accessoryType && SELECTABLE_ACCESSORY_TYPES.includes(args.accessoryType)) {
          openAccessorySelectionDialog(accessoryId);
        }
      }, 500);
    }
    if (args.sectionId != null) {
      const color =
        sectionTintType[args.sectionId] === 'do_not_generate' ? 0xa72121 : '#cc6f1a';
      ahuBoardBus.emit(
        'force:select:section',
        args.sectionId,
        !args.accessoryId,
        !!args.accessoryId,
        color
      );
      dispatch(
        selectSection({ sectionId: args.sectionId, tab: tab as 'ahu' | 'accessories' | 'components' })
      );
      ahuBoardBus.emit('select:section', {
        section: { id: args.sectionId },
        tab: tab as 'ahu' | 'accessories' | 'components',
      });
    }
    const message = args.message?.trim();
    if (
      (message === 'Rating & Selection Incomplete!' ||
        message === 'Invalid selection! Complete all fields.') &&
      !args.accessoryId &&
      args.sectionId != null
    ) {
      const sectionId = args.sectionId;
      window.setTimeout(() => dispatch(openRatingRequest(sectionId)), 100);
    }
  };

  const rowHover = (sectionId: number | null, accessoryId: number | null, on: boolean) => {
    ahuBoardBus.emit('hover:section', {
      section: on && sectionId != null ? { id: sectionId } : null,
      accessory: accessoryId,
    });
  };

  // ---- APPLY (§8.1.1 pipeline) ----
  const apply = async () => {
    if (checkedCount < 1 || resolutionInProgress) return;
    const prevCounts = { ...countsByType };
    dispatch(setResolutionInProgress(true));
    setShowError(false);
    setPercent(0);
    document
      .querySelectorAll('.conected-to-error-log')
      .forEach((el) => el.classList.add('non-clickable'));
    const selectionsList = resolveList
      .map((item, idx) =>
        item.resovable && isChecked(idx)
          ? { value: selections[idx] ?? item.options?.[0]?.value ?? '', item }
          : null
      )
      .filter((x): x is { value: string; item: ResolveListItem } => !!x);

    const result = await applyResolution(
      dispatch,
      () => store.getState(),
      selectionsList,
      ctx,
      { onProgress: setPercent }
    );

    dispatch(setResolutionInProgress(false));
    document
      .querySelectorAll('.conected-to-error-log')
      .forEach((el) => el.classList.remove('non-clickable'));
    ahuBoardBus.emit('complete:errors:resolving', result.needValidateTunnels, result.error, count);
    if (result.error) {
      window.setTimeout(() => setShowError(true), 1000);
    } else {
      const current = store.getState().ahuValidation.countsByType;
      dispatch(showResolveSummary({ prev: prevCounts, current }));
      dispatch(setResolveOpen(true));
    }
    setChecked({});
  };

  // Enter in the panel clicks RESOLVE.
  useEffect(() => {
    if (!resolveOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && resolveOpen) void apply();
    };
    document.addEventListener('keypress', onKey);
    return () => document.removeEventListener('keypress', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveOpen, checkedCount, selections, resolveList]);

  // Scroll sync (list ↔ resolution list, both directions — §8.1).
  const syncScroll = (from: 'list' | 'resolve') => (e: React.UIEvent<HTMLUListElement>) => {
    const target = from === 'list' ? resolveListRef.current : listRef.current;
    if (target) target.scrollTop = e.currentTarget.scrollTop;
  };

  // updateStyles (§8.1 / parity 155): resolve-panel min-width from the
  // longest option text + body zoom; left = (wrapper width + 6)/zoom; ≤1000px.
  useEffect(() => {
    const updateStyles = () => {
      window.setTimeout(() => {
        const zoom = parseFloat(document.body.style.zoom || '') || 1;
        let minWidth = 0;
        document
          .querySelectorAll<HTMLElement>(
            '#resolve-error-log-wrapper select[name="resolution_select"] option, #resolve-error-log-wrapper p.non-resolvable'
          )
          .forEach((el) => {
            const span = document.createElement('span');
            span.style.visibility = 'hidden';
            span.style.position = 'absolute';
            span.style.whiteSpace = 'nowrap';
            span.textContent = el.textContent ?? '';
            document.body.appendChild(span);
            minWidth = Math.max(minWidth, span.offsetWidth);
            span.remove();
          });
        minWidth += 50;
        const wrapper = document.querySelector<HTMLElement>('.error-log-wrapper');
        const width = wrapper?.offsetWidth ?? 0;
        const wrapMinWidth = width > 350 ? 440 : 350;
        const maxWidth = (window.innerWidth - (width + 965)) / zoom;
        const wrapperWidth =
          maxWidth < minWidth || minWidth > wrapMinWidth ? maxWidth : wrapMinWidth;
        const panel = document.getElementById('resolve-error-log-wrapper');
        if (panel) {
          panel.style.minWidth = `${Math.min(wrapperWidth, 1000)}px`;
          panel.style.left = `${(width + 6) / zoom}px`;
        }
        window.setTimeout(() => {
          document
            .querySelector<HTMLElement>(
              '#error_log:not(.opacity_0) .resolve-error-log.actived ~ #resolve-error-log-wrapper #resolution-apply:not(.non-clickable)'
            )
            ?.focus();
        }, 500);
      }, 100);
    };
    updateStyles();
    window.addEventListener('resize', updateStyles);
    return () => window.removeEventListener('resize', updateStyles);
  }, [resolveOpen, resolveList.length]);

  // ---- render -------------------------------------------------------------
  interface RowDef {
    key: string;
    sectionId: number | null;
    tab: string;
    accessoryId: number | null;
    accessoryType?: string;
    type: string | null;
    error?: string;
    message: string;
    label: string;
    clickable: boolean;
  }
  // ORDER CONTRACT (error.log.tmpl): per tunnel → global rows, then per
  // section → accessories, components, dmounts — the resolve panel mirrors
  // this list 1:1 by index (transformErrors uses the same walk).
  const rows: RowDef[] = [];
  flat.forEach((tunnelErr: FlatTunnelErrors) => {
    tunnelErr.global.forEach((g, i) => {
      if (g.message) {
        rows.push({
          key: `g-${tunnelErr.tunnel_id}-${i}`,
          sectionId: null,
          tab: '#show_sections',
          accessoryId: null,
          type: g.type,
          message: g.message,
          label: 'AHU',
          clickable: false,
        });
      }
    });
    tunnelErr.sections.forEach((section) => {
      section.accessories.forEach((a, i) => {
        if (a.message) {
          rows.push({
            key: `a-${section.id}-${a.id}-${i}`,
            sectionId: section.id,
            tab: '#show_accessories',
            accessoryId: Number(a.id),
            accessoryType: String(a.object ?? ''),
            type: a.type,
            message: a.message,
            label: `${section.section} : ${a.item}`,
            clickable: true,
          });
        }
      });
      section.components.forEach((c, i) => {
        if (c.message) {
          rows.push({
            key: `c-${section.id}-${i}`,
            sectionId: section.id,
            tab: '#show_components',
            accessoryId: null,
            type: c.type,
            error: c.error,
            message: c.message,
            label: `${section.section} : ${c.item} `,
            clickable: true,
          });
        }
      });
      (section.dmounts ?? []).forEach((d, i) => {
        if (d.message) {
          rows.push({
            key: `d-${section.id}-${i}`,
            sectionId: section.id,
            tab: '#show_sections',
            accessoryId: null,
            type: d.type,
            message: d.message,
            label: `${section.section} : ${d.item}`,
            clickable: true,
          });
        }
      });
    });
  });

  return (
    <div
      className={`error-log-wrapper dropdown-wrapper${
        count === 0 ? ' non-clickable non-active' : ''
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        id="error_log_ctrl"
        type="checkbox"
        name="error_log_ctrl"
        className="hidden"
        readOnly
        checked={false}
      />
      <div className="title error-title border-bottom">
        {/* QA batch 4 #7 — legacy error.log.tmpl newlines collapse to single
            spaces around "Error Manager"; JSX strips them, so keep them
            explicit ({' '}) or the text abuts the smart-icon. */}
        <span className="smart-icon" /> Error Manager{' '}
        <sup className="smart-label">smart</sup>
        <span
          className={`resolve-error-log${resolveOpen ? ' actived' : ''}`}
          onClick={() => dispatch(setResolveOpen(!resolveOpen))}
        >
          <span className="more">More...</span>
          <span className="less">Less...</span>
        </span>
      </div>
      <div id="errors_list" className="closed-error-log">
        <ul id="errors_list_ul" ref={listRef} onScroll={syncScroll('list')}>
          {rows.map((row, idx) => (
            <li
              key={row.key}
              id="massage_error-item"
              className={`${row.clickable ? 'clickable' : ''}${
                forceHoverIndex === idx ? ' force-hover' : ''
              }`}
              data-id={row.sectionId ?? undefined}
              data-tab={row.tab}
              data-accessory-id={row.accessoryId ?? undefined}
              data-type={row.type ?? undefined}
              data-error={row.error}
              data-message={row.error != null ? row.message : undefined}
              onClick={() => {
                if (row.clickable) {
                  rowClick({
                    sectionId: row.sectionId,
                    tab: row.tab,
                    accessoryId: row.accessoryId,
                    accessoryType: row.accessoryType,
                    error: row.error,
                    message: row.message,
                  });
                }
              }}
              onMouseEnter={() => rowHover(row.sectionId, row.accessoryId, true)}
              onMouseLeave={() => rowHover(null, row.accessoryId, false)}
            >
              <span className="section-name" data-id={row.sectionId ?? undefined}>
                {row.label}
              </span>
              <span className="massage_error" data-id={row.sectionId ?? undefined}>
                {/* legacy tmpl newline between <i> and {{message}} = a space. */}
                <i
                  className={`material-icons ${row.type === 'generate' ? 'warning' : 'error'}`}
                />{' '}
                {row.message}
              </span>
            </li>
          ))}
        </ul>
        <div className="errors-note">
          <div>
            <span className="material-icons error" /> Error
          </div>
          <div>
            <span className="material-icons warning" /> Warning
          </div>
        </div>
      </div>
      <div id="resolve-error-log-wrapper" className={resolveOpen ? 'actived' : ''}>
        <div className="title error-title border-bottom">
          <div className={showResolution ? '' : ' invisible '}>
            <input
              type="checkbox"
              checked={resolveList.every((e, i) => !e.resovable || isChecked(i))}
              name="resolution_input"
              className="resolution_input_all"
              value="all"
              id="resolution_input_all"
              onChange={(e) => {
                const on = e.currentTarget.checked;
                const next: Record<number, boolean> = {};
                resolveList.forEach((_, i) => {
                  next[i] = on;
                });
                setChecked(next);
                setShowError(false);
              }}
            />
            <p>Resolve all</p>
          </div>
        </div>
        <ul
          className="error-resolution-list"
          ref={resolveListRef}
          onScroll={syncScroll('resolve')}
        >
          {resolveList.map((item, idx) => (
            <li
              key={`${item.error_type}-${idx}`}
              className={`error-resolution-li has-values${
                item.only_one_option ? ' label-li' : ''
              } hovered`}
              data-index={idx}
              onMouseEnter={() => {
                setForceHoverIndex(idx);
                rowHover(item.section_id ?? null, item.accessory_id ?? null, true);
              }}
              onMouseLeave={() => {
                setForceHoverIndex(null);
                rowHover(null, item.accessory_id ?? null, false);
              }}
              onClick={(e) => {
                // rowClick: single-option rows toggle their checkbox.
                const target = e.target as HTMLElement;
                if (target.tagName === 'INPUT' || target.tagName === 'SELECT') return;
                if ((item.options?.length ?? 0) > 1) return;
                if (item.resovable) {
                  setChecked((prev) => ({ ...prev, [idx]: !isChecked(idx) }));
                }
              }}
            >
              {item.resovable ? (
                <>
                  <p>
                    <input
                      type="checkbox"
                      name="resolution_input"
                      checked={isChecked(idx)}
                      data-index={idx}
                      id={`resolution_input_${idx}`}
                      data-type={item.type ?? undefined}
                      data-unique={item.unique_id}
                      onChange={(e) => {
                        const on = e.currentTarget.checked;
                        setChecked((prev) => {
                          const next = { ...prev, [idx]: on };
                          // linked-error checkbox pairing via unique ids.
                          if (item.unique_id) {
                            resolveList.forEach((other, oIdx) => {
                              if (other.unique_id === item.unique_id) next[oIdx] = on;
                            });
                          }
                          return next;
                        });
                        setShowError(false);
                      }}
                    />
                  </p>
                  <select
                    data-index={idx}
                    name="resolution_select"
                    className={item.only_one_option ? 'has-one-option' : ''}
                    value={selections[idx] ?? item.options?.[0]?.value ?? ''}
                    onChange={(e) => {
                      // Read the value before the updater: React nulls
                      // `currentTarget` once the handler returns, and the
                      // updater runs later.
                      const value = e.currentTarget.value;
                      setSelections((prev) => ({ ...prev, [idx]: value }));
                    }}
                  >
                    {(item.options ?? []).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label} &nbsp;{' '}
                      </option>
                    ))}
                  </select>
                  <span className="tooltip-text" />
                </>
              ) : item.unique_id ? (
                <p className="non-resolvable has-checkbox">
                  <input
                    type="checkbox"
                    name="resolution_input_disabled"
                    checked={isChecked(idx)}
                    data-index={idx}
                    id={`resolution_input_${idx}`}
                    data-type={item.type ?? undefined}
                    data-unique={item.unique_id}
                    onChange={(e) => {
                      const on = e.currentTarget.checked;
                      setChecked((prev) => {
                        const next = { ...prev, [idx]: on };
                        if (item.unique_id) {
                          resolveList.forEach((other, oIdx) => {
                            if (other.unique_id === item.unique_id) next[oIdx] = on;
                          });
                        }
                        return next;
                      });
                    }}
                  />{' '}
                  {item.text}
                </p>
              ) : (
                <p className="non-resolvable">{item.text}</p>
              )}
            </li>
          ))}
        </ul>
        <div className={`error-message-text${showError ? '' : ' hide'}`}>
          Something went wrong!
        </div>
        <button
          className={`btn${showResolution ? '' : ' invisible'}${
            checkedCount > 0 && !resolutionInProgress ? '' : ' non-clickable'
          }`}
          id="resolution-apply"
          onClick={() => void apply()}
        >
          RESOLVE
        </button>
        <div className={`in-progress-resolving${resolutionInProgress ? ' active' : ''}`}>
          <div className="active preloader dotted-preloader larger" />
          <div className="completed_percent">{percent}%</div>
        </div>
      </div>
      <p className="arrow-right-wrapper">
        <i className="icon-ic-keyboard-arrow-right-black-24px" />
      </p>
    </div>
  );
};

export default ErrorManager;
